import { useEffect, useRef, useState, useCallback } from 'react'
import ngeohash from 'ngeohash'
import { useAdapters } from './useAdapters'
import type { OnlineUser } from '../types/user'
import type { GPSPosition } from '../types/geo'
import { LOCAL_CHANNEL_GEOHASH_PRECISION } from '../types/radio'

const PUBLISH_INTERVAL_MS = 4_000
const IDLE_STOP_MS = 30_000
const STALE_THRESHOLD_MS = 30_000

export function usePresence(position: GPSPosition | null) {
  const { presence: presenceAdapter } = useAdapters()
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([])
  const publishTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unsubRef = useRef<(() => void) | null>(null)
  const mountedRef = useRef(true)
  const currentGeohash = useRef<string>('')

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false; cleanup() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!position) return
    const gh = ngeohash.encode(position.lat, position.lng, LOCAL_CHANNEL_GEOHASH_PRECISION) as string
    if (gh === currentGeohash.current) return
    unsubRef.current?.()
    currentGeohash.current = gh
    unsubRef.current = presenceAdapter.subscribeToPresence(gh, (users) => {
      if (!mountedRef.current) return
      const now = Date.now()
      setOnlineUsers(users.filter((u) => now - u.updatedAt < STALE_THRESHOLD_MS))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.lat, position?.lng, presenceAdapter])

  useEffect(() => {
    if (!position) return
    const publish = () => {
      if (!mountedRef.current) return
      void presenceAdapter.publishPresence({
        lat: position.lat, lng: position.lng, heading: position.heading, speed: position.speed,
        status: position.speed > 0.5 ? 'driving' : 'idle', updatedAt: Date.now(),
      })
    }
    publish()
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    if (position.speed < 0.5) {
      idleTimerRef.current = setTimeout(() => {
        if (publishTimerRef.current) { clearInterval(publishTimerRef.current); publishTimerRef.current = null }
      }, IDLE_STOP_MS)
    } else if (!publishTimerRef.current) {
      publishTimerRef.current = setInterval(publish, PUBLISH_INTERVAL_MS)
    }
  }, [position, presenceAdapter])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (publishTimerRef.current) { clearInterval(publishTimerRef.current); publishTimerRef.current = null }
      } else if (position) {
        void presenceAdapter.publishPresence({
          lat: position.lat, lng: position.lng, heading: position.heading, speed: position.speed,
          status: 'idle', updatedAt: Date.now(),
        })
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [position, presenceAdapter])

  const removePresence = useCallback(async () => {
    cleanup()
    await presenceAdapter.removePresence()
  }, [presenceAdapter])

  function cleanup() {
    if (publishTimerRef.current) { clearInterval(publishTimerRef.current); publishTimerRef.current = null }
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
    unsubRef.current?.()
    unsubRef.current = null
  }

  return { onlineUsers, removePresence }
}
